import { GA_CONFIG, isGaDebugEnabled } from './config';
import {
  getAnalyticsScreen,
  getAnalyticsUserId,
  setAnalyticsScreen,
  setAnalyticsUser,
  clearAnalyticsUser,
  buildAnalyticsContext,
} from './context';
import {
  GA_EVENTS,
  type GaAudioChannel,
  type GaEventName,
  type GaEventPayloads,
  type GaScreen,
} from './events';

type GtagFn = (...args: unknown[]) => void;
type GaParamValue = string | number | boolean;
type GaParams = Record<string, GaParamValue | undefined>;

function getGtag(): GtagFn | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.gtag;
}

function sanitizeParams(params: GaParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (typeof value === 'boolean') {
      out[key] = value ? 1 : 0;
      continue;
    }
    out[key] = value;
  }
  return out;
}

function send(command: string, ...args: unknown[]): void {
  const gtag = getGtag();
  if (!gtag) return;
  gtag(command, ...args);
}

function configureClient(): void {
  send('config', GA_CONFIG.measurementId, {
    send_page_view: false,
    app_name: GA_CONFIG.appName,
    user_id: getAnalyticsUserId(),
  });
}

/** Call once at app boot (main.ts). Safe to call more than once. */
export function initAnalytics(): void {
  if (typeof window === 'undefined') return;
  if (!GA_CONFIG.enabled) return;

  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };
  }

  send('js', new Date());
  configureClient();
}

export function identifyAnalyticsUser(next: {
  userId?: string | null;
  isDemo?: boolean;
}): void {
  setAnalyticsUser(next);
  if (!GA_CONFIG.enabled) return;
  send('set', { user_id: getAnalyticsUserId() ?? null });
  configureClient();
}

export function resetAnalyticsUser(): void {
  clearAnalyticsUser();
  if (!GA_CONFIG.enabled) return;
  send('set', { user_id: null });
}

/**
 * Fire a catalog event. Extra payload keys are merged on top of
 * automatic context (game, is_demo, screen) and the event category.
 */
export function track<K extends GaEventName>(
  event: K,
  payload?: GaEventPayloads[K]
): void {
  if (!GA_CONFIG.enabled) return;
  const def = GA_EVENTS[event];
  if (!def.enabled) return;

  const params = sanitizeParams({
    event_category: def.category,
    ...buildAnalyticsContext(),
    ...(payload as GaParams | undefined),
  });

  if (isGaDebugEnabled()) {
    console.debug('[GA]', def.name, params);
  }

  send('event', def.name, params);
}

export function trackScreen(screen: GaScreen, title?: string): void {
  if (getAnalyticsScreen() === screen) return;
  setAnalyticsScreen(screen);
  if (!GA_CONFIG.enabled || !GA_EVENTS.screen_view.enabled) return;

  const path =
    screen === 'game' ? `/${buildAnalyticsContext().game}` : `/${screen}`;
  const pageTitle = title ?? screen;
  const params = sanitizeParams({
    page_title: pageTitle,
    page_location:
      typeof window !== 'undefined' ? window.location.href : undefined,
    page_path: path,
    screen_name: screen,
    ...buildAnalyticsContext(),
  });

  if (isGaDebugEnabled()) {
    console.debug('[GA] screen', screen, params);
  }

  send('event', 'page_view', params);
}

export function trackSound(muted: boolean, channel: GaAudioChannel): void {
  track(muted ? 'sound_muted' : 'sound_unmuted', { channel });
}

export function trackRoundResult(params: {
  value?: number;
  multiplier?: number | null;
  bet_amount?: number;
  side?: string;
}): void {
  const multiplier = params.multiplier ?? 0;
  const value = params.value ?? 0;
  const isWin = multiplier > 0 || value > 0;
  if (isWin) {
    track('win', {
      value,
      multiplier,
      bet_amount: params.bet_amount,
      side: params.side,
    });
    return;
  }
  track('loss', {
    value,
    multiplier,
    bet_amount: params.bet_amount,
    side: params.side,
  });
}

/** Round bust: always `crashed`, plus `loss` for each uncashed placed bet. */
export function trackCrashBust(params: {
  multiplier: number;
  bets: Record<string, { status?: string; stake?: number }>;
}): void {
  track('crashed', { multiplier: params.multiplier });
  for (const [side, bet] of Object.entries(params.bets)) {
    if (bet.status !== 'placed') continue;
    track('loss', {
      value: 0,
      multiplier: params.multiplier,
      bet_amount: bet.stake,
      side,
    });
  }
}
